<template>
  <div class="page-view">
    <BasePageHeader
      :title="isEdit ? $t('maintenance.form.editTitle') : $t('maintenance.form.newTitle')"
      @back="router.back()"
    >
      <template #actions>
        <button class="btn ghost small" @click="router.back()">{{ $t('common.cancel') }}</button>
        <button class="btn primary small" :disabled="saving || !isValid" @click="submit">
          {{ saving ? $t('common.saving') : (isEdit ? $t('common.save') : $t('common.create')) }}
        </button>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <div v-else-if="loadError" class="state-empty error">{{ loadError }}</div>

    <template v-else>

      <!-- Schedule details -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('maintenance.form.scheduleDetails') }}</div>

        <BaseFormField :label="$t('maintenance.form.title')" required>
          <BaseInput v-model="form.title" :placeholder="$t('ui.eGRsservicePumpeA')" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('maintenance.form.type')">
            <BaseSelect v-model="form.maintenance_type">
              <option value="">{{ $t('common.none') }}</option>
              <option v-for="mt in MAINT_TYPES" :key="mt.value" :value="mt.value">{{ mt.label }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField :label="$t('maintenance.form.frequency')" required>
            <BaseSelect v-model="form.frequency">
              <option value="" disabled>{{ $t('common.select') }}</option>
              <option v-for="f in FREQUENCIES" :key="f.value" :value="f.value">{{ f.label }}</option>
            </BaseSelect>
          </BaseFormField>
        </div>

        <BaseFormField v-if="form.frequency === 'custom'" :label="$t('maintenance.form.everyNDays')" required>
          <BaseInput v-model="form.frequency_days" type="number" min="1" :placeholder="$t('ui.eG90')" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('maintenance.form.nextDueDate')" required>
            <BaseInput v-model="form.next_due_date" type="date" />
          </BaseFormField>
          <BaseFormField :label="$t('maintenance.form.estHours')">
            <BaseInput v-model="form.estimated_hours" type="number" min="0" step="0.5" placeholder="0" />
          </BaseFormField>
        </div>

        <BaseFormField v-if="!isEdit" :label="$t('common.active')">
          <BaseCheckbox v-model="form.is_active" :label="$t('common.active')" />
        </BaseFormField>
      </div>

      <!-- Items -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('maintenance.form.items') }}</div>

        <div v-if="form.items?.length" class="flex flex-wrap gap-1.5 mb-3">
          <span
            v-for="it in form.items" :key="it.id"
            class="badge badge-accent badge-pill flex items-center gap-1"
          >
            {{ it.label }}
            <button
              type="button"
              class="bg-transparent border-0 text-blue-300 cursor-pointer text-sm leading-none p-0 hover:text-red-400"
              @click="removeItem(it.id)"
            >×</button>
          </span>
        </div>
        <BaseFormField :label="$t('maintenance.form.searchItems')" :hint="$t('maintenance.form.itemsHint')">
          <SearchField
            v-model="itemSearch"
            :results="mappedItemResults"
            :loading="itemLoading"
            :debounce="300"
            search-key="sched-items"
            :placeholder="$t('maintenance.form.searchItems')"
            @search="doItemSearch"
            @select="addItem($event.meta)"
          />
        </BaseFormField>
      </div>

      <!-- Notes -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('maintenance.form.notes') }}</div>

        <BaseFormField :label="$t('maintenance.form.notesLabel')">
          <BaseTextarea v-model="form.notes" placeholder="Valgfrie notater…" :rows="3" />
        </BaseFormField>
      </div>

      <!-- Error -->
      <div v-if="saveError" class="mx-4 mt-3 state-empty error">{{ saveError }}</div>

    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from '@/composables/useToast'
import {
  getMaintenanceScheduleById,
  createMaintenanceSchedule,
  updateMaintenanceSchedule,
  searchItemMetadata,
} from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import SearchField from '@/components/search/SearchField.vue'

const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t }  = useI18n()

const id     = route.params.id
const isEdit = !!id

const MAINT_TYPES = computed(() => [
  { value: 'preventive',  label: t('maintenance.types.preventive') },
  { value: 'corrective',  label: t('maintenance.types.corrective') },
  { value: 'inspection',  label: t('maintenance.types.inspection') },
  { value: 'calibration', label: t('maintenance.types.calibration') },
])

const FREQUENCIES = computed(() => [
  { value: 'daily',     label: t('maintenance.frequencies.daily') },
  { value: 'weekly',    label: t('maintenance.frequencies.weekly') },
  { value: 'monthly',   label: t('maintenance.frequencies.monthly') },
  { value: 'quarterly', label: t('maintenance.frequencies.quarterly') },
  { value: 'annual',    label: t('maintenance.frequencies.annual') },
  { value: 'custom',    label: t('maintenance.frequencies.custom') },
])

// ── form state ────────────────────────────────────────────────────────
const loading   = ref(isEdit)
const loadError = ref(null)
const saving    = ref(false)
const saveError = ref(null)

const form = ref({
  title:            '',
  maintenance_type: '',
  frequency:        '',
  frequency_days:   '',
  next_due_date:    '',
  estimated_hours:  '',
  items:            [],
  notes:            '',
  is_active:        true,
})

// ── item search ───────────────────────────────────────────────────────
const itemSearch  = ref('')
const itemResults = ref([])
const itemLoading = ref(false)

const mappedItemResults = computed(() =>
  itemResults.value.map(r => ({
    id: r.id,
    label: instanceLabel(r),
    sublabel: r.item?.name ?? '',
    meta: r,
  }))
)

async function doItemSearch(q) {
  if (!q) { itemResults.value = []; return }
  itemLoading.value = true
  try {
    const res = await searchItemMetadata(q, { page_size: 10 })
    itemResults.value = (res.results ?? res ?? []).filter(
      r => !(form.value.items ?? []).some(s => s.id === r.id)
    )
  } catch {
    itemResults.value = []
  } finally {
    itemLoading.value = false
  }
}

function addItem(inst) {
  if (form.value.items.some(i => i.id === inst.id)) return
  form.value.items.push({ id: inst.id, label: instanceLabel(inst) })
  itemSearch.value  = ''
  itemResults.value = []
}

function removeItem(itemId) {
  form.value.items = form.value.items.filter(i => i.id !== itemId)
}

function instanceLabel(inst) {
  if (!inst) return ''
  if (typeof inst === 'object') return inst.asset_tag ?? inst.serial_number ?? inst.item?.name ?? `#${inst.id}`
  return `#${inst}`
}

// ── load for edit ─────────────────────────────────────────────────────
onMounted(async () => {
  if (!isEdit) return
  try {
    const s = await getMaintenanceScheduleById(id)
    form.value = {
      title:            s.title ?? '',
      maintenance_type: s.maintenance_type ?? '',
      frequency:        s.frequency ?? '',
      frequency_days:   s.frequency_days ?? '',
      next_due_date:    s.next_due_date ?? '',
      estimated_hours:  s.estimated_hours ?? '',
      items:            (s.items ?? []).map(i =>
        typeof i === 'object'
          ? { id: i.id, label: instanceLabel(i) }
          : { id: i, label: `#${i}` }
      ),
      notes:            s.notes ?? s.description ?? '',
      is_active:        s.is_active ?? true,
    }
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('maintenance.form.loadFailed')
  } finally {
    loading.value = false
  }
})

// ── validation ────────────────────────────────────────────────────────
const isValid = computed(() =>
  form.value.title.trim() &&
  form.value.frequency &&
  form.value.next_due_date
)

// ── submit ────────────────────────────────────────────────────────────
async function submit() {
  if (saving.value || !isValid.value) return
  saving.value    = true
  saveError.value = null

  try {
    const payload = {
      title:         form.value.title.trim(),
      frequency:     form.value.frequency,
      next_due_date: form.value.next_due_date,
      items:         form.value.items.map(i => i.id),
    }
    if (form.value.maintenance_type)       payload.maintenance_type = form.value.maintenance_type
    if (form.value.frequency_days !== '')  payload.frequency_days   = parseInt(form.value.frequency_days, 10)
    if (form.value.estimated_hours !== '') payload.estimated_hours  = form.value.estimated_hours
    if (form.value.notes?.trim())          payload.notes            = form.value.notes.trim()
    if (!isEdit)                           payload.is_active        = form.value.is_active

    if (isEdit) {
      await updateMaintenanceSchedule(id, payload)
      toast.success(t('maintenance.form.updated'))
    } else {
      await createMaintenanceSchedule(payload)
      toast.success(t('maintenance.form.created'))
    }
    router.push('/maintenance/schedules')
  } catch (e) {
    const d = e?.data
    if (d && typeof d === 'object') {
      const msgs = Object.entries(d)
        .filter(([k]) => k !== 'status_code')
        .flatMap(([, v]) => Array.isArray(v) ? v : [v])
        .filter(Boolean)
        .join(' ')
      saveError.value = msgs || e?.message || t('maintenance.form.saveFailed')
    } else {
      saveError.value = e?.message ?? t('maintenance.form.saveFailed')
    }
  } finally {
    saving.value = false
  }
}
</script>
